import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { create, StoreApi } from 'zustand';
import { Account } from './use-account-store';
import { Trade } from '@/features/markets/api/get-trades';
import { AssetType, MarketStatus } from '@/types';
import { isEqual } from '@/utils/lodash-alternatives';

export const COLLATERAL_SYMBOL = 'USD';
export const COLLATERAL_DECIMALS = 6n; // TODO: maybe env var
export const FUNDING_RATE_DECIMALS = 8n;
export const CUM_FUNDING_DECIMALS = 12n;

export interface MarketData {
  markPrice: bigint;
  midPrice: bigint;
  indexPrice: bigint;
  oneHrFundingRate: bigint;
  priceChange: bigint;
  priceChangePct: bigint;
  cumFunding: bigint;
  volume?: bigint;
  quoteVolume?: bigint;
  openInterest?: bigint;
  updatedAtMs: number;
  midUpdatedMs: number;
}

export interface MarketSpec {
  displayName: string;
  displaySymbol: string;
  base: string;
  quote: string;
  sizeDecimals: bigint;
  priceDecimals: bigint;
  initMarginRatio: bigint;
  maintMarginRatio: bigint;
  takerFee: bigint;
  isolated: boolean;
  status: MarketStatus;
  asset: AssetType;
  tickSizes: string[];
  defaultTickSize: string;
}

export interface OrderBook {
  bids: [bigint, bigint][]; // price, qty (sorted ascending)
  asks: [bigint, bigint][]; // price, qty (sorted descending)
}

export interface MarketStore {
  marketData: MarketData;
  marketSpec: MarketSpec;
  book: OrderBook;
  books: Record<string, OrderBook>;
  trades: Trade[];
  leverage: number | null;
  activeTickSize: string | null;
  setTrades: (trades: Trade[]) => void;
  setMarketData: (marketData: Partial<MarketData>) => void;
  setMarketSpec: (marketSpecs: Partial<MarketSpec>) => void;
  setBook: (book: OrderBook, tickSize?: string) => void;
  setActiveTickSize: (tickSize: string | null) => void;
}

const marketStores: Record<string, StoreApi<MarketStore>> = {};

const _createMarketStore = () =>
  create<MarketStore>((set, get) => ({
    marketData: {
      markPrice: 0n,
      midPrice: 0n,
      indexPrice: 0n,
      oneHrFundingRate: 0n,
      priceChange: 0n,
      priceChangePct: 0n,
      cumFunding: 0n,
      updatedAtMs: 0,
      midUpdatedMs: 0,
    },
    marketSpec: {
      displayName: '',
      displaySymbol: '',
      base: '',
      quote: '',
      sizeDecimals: 0n,
      priceDecimals: 0n,
      initMarginRatio: 0n,
      maintMarginRatio: 0n,
      takerFee: 0n,
      isolated: false,
      status: MarketStatus.TRADING,
      asset: AssetType.STOCK,
      tickSizes: [],
      defaultTickSize: '',
    },
    leverage: null,
    activeTickSize: null,
    book: { bids: [], asks: [] },
    books: {},
    trades: [],
    setTrades: (trades) => set({ trades }),
    setMarketData: (marketData) =>
      set((state) => ({ marketData: { ...state.marketData, ...marketData } })),
    setMarketSpec: (marketSpec) =>
      set((state) => ({ marketSpec: { ...state.marketSpec, ...marketSpec } })),
    setBook: (book, tickSize) =>
      set((state) =>
        tickSize ? { books: { ...state.books, [tickSize]: book } } : { book },
      ),
    setActiveTickSize: (tickSize) => set({ activeTickSize: tickSize }),
  }));

export const useMarketStore = (symbol: string) => {
  if (!marketStores[symbol]) {
    marketStores[symbol] = _createMarketStore();
  }
  return marketStores[symbol];
};

export const createMarketStore = (symbol: string) => {
  if (!marketStores[symbol]) {
    marketStores[symbol] = _createMarketStore();
  }
};

export const getAllSymbols = (options?: { asset?: AssetType }) => {
  const symbols = Object.keys(marketStores);
  if (!options?.asset) {
    return symbols;
  }
  return symbols.filter(
    (key) => marketStores[key].getState().marketSpec.asset === options.asset,
  );
};

/**
 * @deprecated
 */
export function useMarketStores<T>(
  selector: (state: MarketStore) => T,
  symbols?: string[], // DEPRECATED // WARNING: if symbols changes, the stores might not update before you access a new symbol
) {
  const symbolsToSub = useMemo(() => symbols || getAllSymbols(), [symbols]);

  const [marketStoreSlices, setMarketStoreSlices] = useState<Record<string, T>>(
    (symbols || getAllSymbols()).reduce(
      (acc, symbol) => {
        acc[symbol] = selector(useMarketStore(symbol).getState());
        return acc;
      },
      {} as Record<string, T>,
    ),
  );
  const storeSubs = useRef<Record<string, () => void>>({});

  const updateMarketStoreSlice = useCallback(
    (symbol: string) => {
      const store = useMarketStore(symbol);
      const slice = selector(store.getState());
      setMarketStoreSlices((prev) => {
        if (isEqual(prev[symbol], slice)) {
          return prev;
        }
        return {
          ...prev,
          [symbol]: slice,
        };
      });
    },
    [selector],
  );

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    const debouncedUpdate = () => {
      if (!timeoutId) {
        timeoutId = setTimeout(() => {
          symbolsToSub.forEach(updateMarketStoreSlice);
          timeoutId = null;
        }, 125);
      }
    };

    symbolsToSub.forEach((symbol) => {
      const store = useMarketStore(symbol);
      storeSubs.current[symbol] = store.subscribe(debouncedUpdate);
    });

    return () => {
      Object.values(storeSubs.current).forEach((unsubscribe) => unsubscribe());
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [symbolsToSub]);

  // const isReady = useMemo(() => {
  //   const marketSymbols = new Set(Object.keys(marketStoreSlices));
  //   const symbols = new Set(symbolsToSub);
  //   return symbols.symmetricDifference(marketSymbols).size === 0;
  // }, [symbolsToSub, Object.keys(marketStoreSlices)]);

  return marketStoreSlices;
}

export const getAccountMarketStores = <T>(
  account: Account,
  marketStoresSlices: Record<string, T>,
) => {
  const accountMarkets = Object.keys(marketStoresSlices)
    .filter((symbol) => symbol in account.positions)
    .reduce(
      (acc, symbol) => {
        acc[symbol] = marketStoresSlices[symbol];
        return acc;
      },
      {} as Record<string, T>,
    );
  return accountMarkets;
};

export const getAllMarketStoreStates = () => {
  return Object.keys(marketStores).reduce(
    (acc, symbol) => {
      acc[symbol] = useMarketStore(symbol).getState();
      return acc;
    },
    {} as Record<string, MarketStore>,
  );
};
